import React, { useState, useEffect } from 'react';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import './App.css';
import { apiGet, apiPost } from './utils/api';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [daysToChristmas, setDaysToChristmas] = useState('');
  const [animationTheme, setAnimationTheme] = useState('original');

  // Función para leer cookie del navegador
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  const verifyToken = async () => {
    
    // Primero verificar si tenemos cookie local
    const cookieToken = getCookie('token');
    
    if (!cookieToken) {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    try {
      const response = await apiGet('/api/verify-token');

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setIsAuthenticated(true);
      } else {
        const errorData = await response.json();
        
        // Solo limpiar estado si es un error de autenticación real
        if (response.status === 401) {
          setUser(null);
          setIsAuthenticated(false);
        } else {
          // No cambiar el estado para errores de servidor
        }
      }
    } catch (error) {
      
      // Solo limpiar estado para errores de conexión críticos
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // Cargar tema de animación al montar el componente
  useEffect(() => {
    const loadAnimationTheme = async () => {
      try {
        const response = await apiGet('/api/animation-theme');
        if (response.ok) {
          const data = await response.json();
          setAnimationTheme(data.animationTheme || 'original');
        }
      } catch (error) {
        // Si hay error, usar tema por defecto
        setAnimationTheme('original');
      }
    };
    
    loadAnimationTheme();
    
    // Listener para detectar cambios de tema sin recargar la página
    const handleThemeChange = async () => {
      await loadAnimationTheme();
    };
    
    window.addEventListener('animationThemeChanged', handleThemeChange);
    
    return () => {
      window.removeEventListener('animationThemeChanged', handleThemeChange);
    };
  }, []);

  // Aplicar clase CSS al body según el tema activo
  useEffect(() => {
    // Remover clases de temas anteriores
    document.body.classList.remove('theme-original', 'theme-navidad', 'theme-dia-muertos', 'theme-independencia-mexico');
    
    // Agregar clase del tema actual
    if (animationTheme === 'navidad') {
      document.body.classList.add('theme-navidad');
    } else if (animationTheme === 'dia-muertos') {
      document.body.classList.add('theme-dia-muertos');
    } else if (animationTheme === 'independencia-mexico') {
      document.body.classList.add('theme-independencia-mexico');
    } else {
      document.body.classList.add('theme-original');
    }
    
    // Cleanup al desmontar
    return () => {
      document.body.classList.remove('theme-original', 'theme-navidad', 'theme-dia-muertos', 'theme-independencia-mexico');
    };
  }, [animationTheme]);

  // Calcular días hasta Navidad
  useEffect(() => {
    const calculateDaysToChristmas = () => {
      const today = new Date();
      const xmas = new Date(today.getFullYear(), 11, 25); // 25 de diciembre
      
      // Si ya pasó Navidad este año, calcular para el próximo año
      if (xmas < today) {
        xmas.setFullYear(today.getFullYear() + 1);
      }
      
      const diffTime = xmas - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        setDaysToChristmas("¡Es Navidad!! ¡Feliz Navidad!");
      } else if (diffDays < 0) {
        setDaysToChristmas(`Navidad fue hace ${Math.abs(diffDays)} días.`);
      } else {
        setDaysToChristmas(`¡${diffDays} días para Navidad!`);
      }
    };
    
    calculateDaysToChristmas();
  }, []);

  // Efecto para verificar token solo al montar el componente
  useEffect(() => {
    // Verificar si hay un token en cookies (solo al cargar la app)
    verifyToken();
  }, []); // Ejecutar solo una vez al montar

  // Efecto separado para manejar eventos de visibilidad (no debe verificar token)
  useEffect(() => {
    // Detectar cuando la ventana vuelve a estar activa (solo para logging)
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated) {
        // No hacer verificación automática - se validará en la próxima acción del usuario
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup del evento al desmontar
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);

  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    // Marcar tiempo de login para detectar logout automático
    window.lastLoginTime = Date.now();
  };

  const handleLogout = async () => {
    
    // SOLUCIÓN FINAL: No limpiar cookies en logout - son permanentes del dispositivo
    try {
      await apiPost('/api/logout');
    } catch (error) {
    } finally {
      // Solo limpiar estado de sesión, NO la cookie
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Verificando autenticación...</p>
      </div>
    );
  }

  return (
    <div className="App">
      {/* Neural Background Effects */}
      <div className="neural-background"></div>
      
      {/* Animations based on theme */}
      {animationTheme === 'navidad' && (
        <>
          {/* Christmas Tree Animation */}
          <ul className="estrella">
            <li></li>
            <li></li>
            <li></li>
            <li></li>
            <li></li>
          </ul>

          <ul className="arbol">
            {Array.from({ length: 200 }, (_, i) => (
              <li key={i}></li>
            ))}
          </ul>

          {/* Christmas Counter */}
          <div className="christmas-counter">
            <p id="days">{daysToChristmas}</p>
          </div>
        </>
      )}
      
      {animationTheme === 'dia-muertos' && (
        <>
          {/* Floating Geometric Shapes - Day of the Dead */}
          <div className="geometric-shapes dia-muertos-theme">
            <div className="shape"></div>
            <div className="shape"></div>
            <div className="shape"></div>
            <div className="shape"></div>
          </div>

          {/* Floating Day of the Dead Emojis */}
          <div className="floating-emojis">
            <div className="emoji-item">🏵️</div>
            <div className="emoji-item">🕯️</div>
            <div className="emoji-item">💀</div>
            <div className="emoji-item">🕷️</div>
            <div className="emoji-item">🌼</div>
            <div className="emoji-item">🏵️</div>
            <div className="emoji-item">🕯️</div>
            <div className="emoji-item">💀</div>
            <div className="emoji-item">🕷️</div>
            <div className="emoji-item">🌼</div>
          </div>
        </>
      )}

      {animationTheme === 'independencia-mexico' && (
        <>
          <div className="geometric-shapes independencia-mexico-theme">
            <div className="shape">🇲🇽</div>
            <div className="shape">🇲🇽</div>
            <div className="shape">🇲🇽</div>
            <div className="shape independencia-triangle"></div>
          </div>
        </>
      )}
      
      {animationTheme === 'original' && (
        <>
          {/* Floating Geometric Shapes - Original Neural Glass */}
          <div className="geometric-shapes">
            <div className="shape"></div>
            <div className="shape"></div>
            <div className="shape"></div>
            <div className="shape"></div>
          </div>
        </>
      )}
      
      {/* Neural Network Lines - Always shown */}
      <div className="neural-lines">
        <div className="neural-line"></div>
        <div className="neural-line"></div>
        <div className="neural-line"></div>
      </div>

      {isAuthenticated ? (
        <Dashboard user={user} onLogout={handleLogout} />
      ) : (
        <Login onLogin={handleLogin} />
      )}
    </div>
  );
}

export default App;